import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Info, Plus } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { ContentCard, ui } from "../../../components/ui";
import { conveyanceClaimService } from "../../../services/conveyanceClaimService";
import sessionDB from "../../../utils/sessionDB";
import { getAuthenticatedEmployeeId } from "../../../lib/employee";
import {
  CLAIMS_LIST_PATH,
  CLAIM_CATEGORY,
  CLAIM_STATUS,
  MAX_TOTAL_AMOUNT,
  abbreviationFor,
  defaultCurrency,
  formatAmount,
  isSubmitterWritable,
} from "../constants/claimConstants";
import { CLAIM_TYPE } from "../constants/conveyanceConstants";
import {
  computeClaimTotal,
  emptyTripLine,
  firstOfMonth,
  modeTransportForId,
  monthKeyOf,
  toIsoDate,
  tripDateBounds,
  tripLineFromRow,
  tripLinePayload,
  validateTripLines,
} from "../lib/claimRules";
import { ClaimStatusBadge } from "../components/ClaimStatusBadge";
import {
  ClaimBlockedScreen,
  ClaimLoadingScreen,
  useConveyancePolicy,
} from "../components/PolicyGate";
import { Toast } from "../components/Toast";
import { TripLineCard } from "../components/TripLineCard";
import { selectBaseClass } from "../components/formControls";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { cn } from "../../../lib/utils";

/**
 * Screen 3 — Claim Submission Form: Rate per km (ESS).
 *
 * ONE HEADER, MANY DATED TRIP LINES. The approval unit is the whole claim, so
 * there is no line-level action anywhere on this screen. Every line must fall
 * in the same calendar month, and that month locks to the first line saved.
 *
 * What the policy drives (nothing below is fixed in this file): the modes the
 * dropdown offers and their rates, whether tolls/parking columns exist, and
 * the date window via submission_deadline_day. The currency beside every
 * amount comes from the metrics master, and its id is stored on the claim.
 *
 * The claim is saved whole: header, every trip line and each line's proof go
 * in one request, so a removed line is simply absent from the next save.
 */
export function RatePerKmClaimPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { claimId } = useParams();
  const isEdit = Boolean(claimId);

  const {
    loading: policyLoading,
    error: policyError,
    policy,
    modes,
    modeOptions,
    currencies,
    isConfigured,
    reload: reloadPolicy,
  } = useConveyancePolicy();

  const empId = useMemo(() => {
    const fromSession = Number(sessionDB.getEmpId());
    if (Number.isFinite(fromSession) && fromSession > 0) return fromSession;
    return getAuthenticatedEmployeeId();
  }, []);

  const [claim, setClaim] = useState(null);
  const [lines, setLines] = useState([emptyTripLine()]);
  const [loadingClaim, setLoadingClaim] = useState(isEdit);
  const [loadError, setLoadError] = useState("");
  const [linesError, setLinesError] = useState("");

  const [metricId, setMetricId] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [locationsByCustomer, setLocationsByCustomer] = useState({});
  const [loadingLocationsFor, setLoadingLocationsFor] = useState(() => new Set());

  const [problems, setProblems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [toast, setToast] = useState(null);

  /* --------------------------------------------------------- load claim */

  useEffect(() => {
    if (!isEdit) return;
    setLoadingClaim(true);
    conveyanceClaimService
      .getClaimById(claimId)
      .then(async (loadedClaim) => {
        setClaim(loadedClaim);

        // The claim endpoint returns the header alone, so the trips are read
        // separately; `lineDetail` is preferred for the day it starts carrying
        // them.
        let loadedLines = loadedClaim?.lineDetail;
        if (!loadedLines) {
          try {
            loadedLines = await conveyanceClaimService.fetchTripLines(claimId);
          } catch (error) {
            // Failing to READ the lines has to block WRITING them: a save
            // sends the grid as the claim's complete set, so saving now would
            // replace the stored trips with an empty one. The form opens
            // read-only and says why instead.
            console.error("Trip line lookup failed:", error);
            setLinesError(error.message || t("conveyanceClaim.errorLoadLines"));
            setLines([]);
            return;
          }
        }

        // A claim saved with no lines still needs one blank row to edit.
        // `modes` maps each line's stored mode_id back to its mode string.
        const mappedLines = loadedLines.length
          ? loadedLines.map((row) => tripLineFromRow(row, modes))
          : [emptyTripLine()];
        setLines(mappedLines);

        // Pre-fetch CRM data for any line that already carries a customerId
        // (Customer Site Visit). Without this the Select components have no
        // items to match the stored id against and show the placeholder
        // instead of the customer name / area name.
        const customerIds = [
          ...new Set(
            mappedLines
              .filter((l) => l.customerId)
              .map((l) => String(l.customerId))
          ),
        ];
        if (customerIds.length) {
          // Load the customer list so the Customer dropdown can display names.
          if (!requestedCrm.current.customers) {
            requestedCrm.current.customers = true;
            setCustomersLoading(true);
            conveyanceClaimService
              .fetchCustomers()
              .then(setCustomers)
              .catch((error) => {
                console.warn("Customer pre-fetch failed:", error);
                requestedCrm.current.customers = false;
              })
              .finally(() => setCustomersLoading(false));
          }

          // Load locations for every referenced customer so the Area dropdown
          // can display the stored area name immediately.
          customerIds.forEach((key) => {
            if (requestedCrm.current.locations.has(key)) return;
            requestedCrm.current.locations.add(key);
            setLoadingLocationsFor((pending) => new Set(pending).add(key));
            conveyanceClaimService
              .fetchCustomerLocations(key)
              .then((rows) =>
                setLocationsByCustomer((next) => ({ ...next, [key]: rows }))
              )
              .catch((error) => {
                console.warn("Location pre-fetch failed:", error);
                requestedCrm.current.locations.delete(key);
              })
              .finally(() =>
                setLoadingLocationsFor((pending) => {
                  const next = new Set(pending);
                  next.delete(key);
                  return next;
                })
              );
          });
        }
      })
      .catch((error) => {
        console.error("Failed to load claim:", error);
        setLoadError(error.message || t("conveyanceClaim.errorLoadClaim"));
      })
      .finally(() => setLoadingClaim(false));
    // `modes` is read only to map each line's stored mode_id on first load.
    // Refetching the claim when the policy modes arrive would discard the
    // user's edits, so it is left out on purpose — the effect below resolves
    // any mode that could not be mapped yet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId, isEdit, t]);

  /* ------------------------------------------------------------ CRM data */

  /**
   * CRM is only queried when a trip line actually asks for it — the customer
   * list on first open of a Customer dropdown, a customer's areas on first open
   * of its Area dropdown. A claim that never uses the Customer / Site Visit
   * reason makes no CRM call at all.
   */
  const requestedCrm = useRef({ customers: false, locations: new Set() });

  const loadCustomers = useCallback(() => {
    if (requestedCrm.current.customers) return;
    requestedCrm.current.customers = true;

    setCustomersLoading(true);
    conveyanceClaimService
      .fetchCustomers()
      .then(setCustomers)
      .catch((error) => {
        console.warn("Customer lookup failed:", error);
        requestedCrm.current.customers = false;
      })
      .finally(() => setCustomersLoading(false));
  }, []);

  const loadLocations = useCallback((customerId) => {
    if (!customerId) return;
    const key = String(customerId);
    if (requestedCrm.current.locations.has(key)) return;
    requestedCrm.current.locations.add(key);

    setLoadingLocationsFor((pending) => new Set(pending).add(key));
    conveyanceClaimService
      .fetchCustomerLocations(key)
      .then((rows) => setLocationsByCustomer((next) => ({ ...next, [key]: rows })))
      .catch((error) => {
        console.warn("Customer location lookup failed:", error);
        requestedCrm.current.locations.delete(key);
      })
      .finally(() =>
        setLoadingLocationsFor((pending) => {
          const next = new Set(pending);
          next.delete(key);
          return next;
        })
      );
  }, []);

  /* ------------------------------------------------------- derived state */

  // Starts on the default currency; the user can change it in the claim
  // header, and an existing claim keeps the metric it was saved with.
  useEffect(() => {
    if (metricId != null) return;
    const fromClaim = claim?.metricId;
    if (fromClaim != null) setMetricId(fromClaim);
    else if (currencies.length) setMetricId(defaultCurrency(currencies)?.metricId ?? null);
  }, [claim, currencies, metricId]);

  // A stored line persists mode_id, not the mode label. If the policy modes
  // finished loading only AFTER the claim was mapped, resolve the label now —
  // touching only lines still missing a mode, so a user edit is never undone.
  useEffect(() => {
    if (!modes.length) return;
    setLines((current) => {
      let changed = false;
      const next = current.map((line) => {
        if (line.mode || line.modeId == null) return line;
        const resolved = modeTransportForId(modes, line.modeId);
        if (!resolved) return line;
        changed = true;
        return { ...line, mode: resolved };
      });
      return changed ? next : current;
    });
  }, [modes, lines]);

  // After the first save a new claim has an id, so a second Save as Draft
  // must update it rather than raise another claim (and orphan its lines).
  const activeClaimId = claim?.claimId ?? claimId ?? null;

  const status = claim?.claimStatus ?? CLAIM_STATUS.DRAFT;
  // Lines that would not load make the whole form read-only, not just the
  // buttons: nothing may be edited into a grid that cannot be saved back.
  const readOnly = (isEdit && !isSubmitterWritable(status)) || Boolean(linesError);
  const abbreviation = abbreviationFor(currencies, metricId);

  /**
   * The deadline governs FIRST submission only. A claim already in Sent Back
   * was submitted before the deadline, so last-month dates stay open for it.
   */
  const bounds = useMemo(
    () =>
      tripDateBounds({
        deadlineDay: policy?.submissionDeadlineDay,
        allowLastMonth: status === CLAIM_STATUS.SENT_BACK ? true : undefined,
      }),
    [policy, status]
  );

  const claimTotal = useMemo(
    () => computeClaimTotal(lines, modes, policy),
    [lines, modes, policy]
  );

  /** Locked to the first line that carries a date — the header never sets it. */
  const claimMonth = useMemo(() => {
    const dated = lines.find((line) => line.tripDate);
    return dated ? monthKeyOf(dated.tripDate) : "";
  }, [lines]);

  /** Validation problems, indexed by line number then field, for the rows. */
  const errorsByLine = useMemo(() => {
    const map = {};
    problems.forEach((problem) => {
      if (!problem.lineNo || !problem.field) return;
      map[problem.lineNo] = { ...map[problem.lineNo], [problem.field]: t(problem.messageKey) };
    });
    return map;
  }, [problems, t]);

  const overCap = claimTotal > MAX_TOTAL_AMOUNT;

  /* -------------------------------------------------------------- edits */

  const updateLine = (index, next) => {
    setLines((current) => current.map((line, i) => (i === index ? next : line)));
  };

  const addLine = () => setLines((current) => [...current, emptyTripLine()]);

  // Nothing to track on removal: the claim is saved whole, so a line the user
  // dropped is simply absent from the next lineDetail.
  const removeLine = (index) => {
    setLines((current) =>
      current.length === 1 ? [emptyTripLine()] : current.filter((_, i) => i !== index)
    );
  };

  /* --------------------------------------------------------------- save */

  const buildPayload = async (claimStatus) => {
    const firstDated = lines.find((line) => line.tripDate)?.tripDate ?? null;
    return {
      category: CLAIM_CATEGORY.RATE_PER_KM,
      // Both are sent: claim_date is the first trip's actual date, claim_month
      // the month every line on the claim shares.
      claimDate: firstDated ? toIsoDate(firstDated) : null,
      claimMonth: firstDated ? toIsoDate(firstOfMonth(firstDated)) : null,
      totalAmount: claimTotal,
      claimStatus,
      policyId: policy?.policyId ?? null,
      metricId: metricId ?? null,
      empId,
      // An update states the claim it is updating in the body as well as the
      // path; a create has no id to state.
      ...(activeClaimId ? { claimId: activeClaimId, modifiedBy: empId } : { createdBy: empId }),
      // The claim's complete set of trips, in grid order — position is the
      // line number. Encoding each line's proof is async, hence the await.
      lineDetail: await Promise.all(
        lines.map((line) =>
          tripLinePayload(line, { modes, policy, metricId, claimId: activeClaimId })
        )
      ),
    };
  };

  /**
   * One request: the claim and every trip on it are saved together, so there
   * is no window where a header exists without its lines, and nothing needs
   * reading back afterwards.
   */
  const persist = async (claimStatus) => {
    setSaving(true);
    setSaveError("");

    try {
      const payload = await buildPayload(claimStatus);
      const savedClaim = activeClaimId
        ? await conveyanceClaimService.updateClaim(activeClaimId, payload)
        : await conveyanceClaimService.createClaim(payload);

      // No read-back: the claim was saved whole, so what is on screen is what
      // was stored. The grid is only refilled when the response echoes the
      // saved lines, which is the one case where it can gain their ids.
      setClaim(savedClaim);
      if (savedClaim?.lineDetail?.length) {
        setLines(savedClaim.lineDetail.map((row) => tripLineFromRow(row, modes)));
      }
      setToast({
        tone: "success",
        message:
          claimStatus === CLAIM_STATUS.DRAFT
            ? t("conveyanceClaim.draftSaved")
            : t("conveyanceClaim.claimSubmitted"),
      });
      // Both Save as Draft and Submit end on the claims list, once the toast
      // has been seen. A saved draft is reopened from there by id, which also
      // means this form can never be left holding a claim it has already
      // created and would create again on the next save.
      setTimeout(() => navigate(CLAIMS_LIST_PATH), 900);
    } catch (error) {
      console.error("Failed to save claim:", error);
      setSaveError(error.message || t("conveyanceClaim.errorSaveClaim"));
    } finally {
      setSaving(false);
    }
  };

  /** Draft save skips line validation entirely — an incomplete claim may rest. */
  const handleSaveDraft = () => {
    setProblems([]);
    if (overCap) {
      setSaveError(t("conveyanceClaim.errorTotalOverCap", { max: MAX_TOTAL_AMOUNT }));
      return;
    }
    persist(CLAIM_STATUS.DRAFT);
  };

  const handleSubmit = () => {
    setSaveError("");
    const found = validateTripLines(lines, { modes, policy, bounds });
    setProblems(found);
    if (found.length > 0) return;
    if (overCap) {
      setSaveError(t("conveyanceClaim.errorTotalOverCap", { max: MAX_TOTAL_AMOUNT }));
      return;
    }
    persist(CLAIM_STATUS.SUBMITTED);
  };

  /* ------------------------------------------------------------- gating */

  if (policyLoading || loadingClaim) return <ClaimLoadingScreen />;

  if (policyError) {
    return (
      <ClaimBlockedScreen
        title={t("conveyanceClaim.policyUnavailableTitle")}
        message={policyError}
        onRetry={reloadPolicy}
      />
    );
  }

  if (!isConfigured) {
    return (
      <ClaimBlockedScreen
        title={t("conveyanceClaim.noPolicyTitle")}
        message={t("conveyanceClaim.noPolicyMessage")}
        onRetry={reloadPolicy}
      />
    );
  }

  // Per-km and fixed monthly are mutually exclusive per tenant.
  if (policy.claimType !== CLAIM_TYPE.RATE_PER_KM) {
    return (
      <ClaimBlockedScreen
        title={t("conveyanceClaim.categoryUnavailableTitle")}
        message={t("conveyanceClaim.perKmUnavailableMessage")}
      />
    );
  }

  if (loadError) {
    return (
      <ClaimBlockedScreen
        title={t("conveyanceClaim.errorLoadClaim")}
        message={loadError}
      />
    );
  }

  return (
    <AppLayout className="space-y-5">
      {toast ? (
        <Toast tone={toast.tone} onDone={() => setToast(null)}>
          {toast.message}
        </Toast>
      ) : null}

      <PageBackLink to={CLAIMS_LIST_PATH} />

      <div>
        <h1 className={ui.pageTitle}>{t("conveyanceClaim.perKmFormTitle")}</h1>
      </div>

      {/* Sent Back reason banner. The approver's reason lives in
          expense_claim_action, which has no read endpoint yet, so the banner
          announces the state without inventing the text. */}
      {status === CLAIM_STATUS.SENT_BACK ? (
        <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
          <div>
            <p className="text-sm font-bold text-amber-900">{t("conveyanceClaim.sentBackTitle")}</p>
            <p className="mt-0.5 text-[12px] text-amber-800">{t("conveyanceClaim.sentBackMessage")}</p>
          </div>
        </div>
      ) : null}

      {/* Header block */}
      <ContentCard title={t("conveyanceClaim.claimHeader")} bodyClassName="p-4 sm:p-5">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <p className={ui.sectionLabel}>{t("conveyanceClaim.claimNo")}</p>
            <p className="mt-1 text-sm font-bold text-foreground">
              {claim?.claimId ? `#${claim.claimId}` : t("conveyanceClaim.claimNoOnSave")}
            </p>
          </div>
          <div>
            <p className={ui.sectionLabel}>{t("conveyanceClaim.claimMonth")}</p>
            <p className="mt-1 text-sm font-bold text-foreground">
              {claimMonth || t("conveyanceClaim.claimMonthFromFirstLine")}
            </p>
          </div>
          <div>
            {/* The claim's one currency. Chosen here and nowhere else: every
                amount on every trip line is denominated in it. */}
            <p className={ui.sectionLabel}>{t("conveyanceApproval.currency")}</p>
            <div className="mt-1">
              <Select
                value={metricId == null ? undefined : String(metricId)}
                disabled={readOnly || currencies.length === 0}
                onValueChange={(next) => setMetricId(Number(next))}
              >
                <SelectTrigger className={cn(selectBaseClass, "h-9 border-[var(--color-text-border)]")}>
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent className="max-h-60 overflow-y-auto">
                  {currencies.map((currency) => (
                    <SelectItem key={currency.metricId} value={String(currency.metricId)}>
                      {currency.abbreviation}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <p className={ui.sectionLabel}>{t("conveyanceClaim.claimTotal")}</p>
            <p className="mt-1 text-sm font-extrabold text-foreground">
              {formatAmount(claimTotal, abbreviation)}
            </p>
          </div>
          <div>
            <p className={ui.sectionLabel}>{t("conveyanceClaim.status")}</p>
            <div className="mt-1">
              <ClaimStatusBadge status={status} />
            </div>
          </div>
        </div>
      </ContentCard>

      {/* Validation summary — line number plus the failing field, per line */}
      {problems.length > 0 ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
          <p className="text-sm font-bold text-rose-800">
            {t("conveyanceClaim.validationSummaryTitle")}
          </p>
          <ul className="mt-2 space-y-1">
            {problems.map((problem, index) => (
              <li key={`${problem.lineNo}-${problem.field}-${index}`} className="text-[12px] text-rose-700">
                {problem.lineNo
                  ? `${t("conveyanceClaim.tripLineNo", { number: problem.lineNo })}: ${t(problem.messageKey)}`
                  : t(problem.messageKey)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {saveError ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
          <p className="text-sm font-semibold text-rose-700">{saveError}</p>
        </div>
      ) : null}

      <div className="flex items-start gap-2.5 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3">
        <Info className="h-4 w-4 mt-0.5 shrink-0 text-sky-600" />
        <p className="text-[12px] text-sky-900">{t("conveyanceClaim.proofsNotStoredNotice")}</p>
      </div>

      {/* Trip lines */}
      <div className="space-y-4">
        {lines.map((line, index) => (
          <TripLineCard
            key={line.key}
            line={line}
            index={index}
            modes={modes}
            modeOptions={modeOptions}
            policy={policy}
            bounds={bounds}
            currencies={currencies}
            metricId={metricId}
            customers={customers}
            customersLoading={customersLoading}
            onCustomersNeeded={loadCustomers}
            locations={locationsByCustomer[String(line.customerId)] || []}
            locationsLoading={loadingLocationsFor.has(String(line.customerId))}
            onLocationsNeeded={loadLocations}
            errors={errorsByLine[index + 1] || {}}
            disabled={readOnly || saving}
            canRemove={!readOnly}
            onChange={(next) => updateLine(index, next)}
            onRemove={() => removeLine(index)}
          />
        ))}
      </div>

      {!readOnly ? (
        <button
          type="button"
          onClick={addLine}
          disabled={saving}
          className="w-full h-12 rounded-2xl border border-dashed border-[var(--color-text-border)] text-sm font-semibold text-[var(--color-text-secondary)] hover:border-[var(--color-primary)]/40 hover:text-[var(--color-primary)] transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
        >
          <Plus className="h-4 w-4" />
          {t("conveyanceClaim.addTripLine")}
        </button>
      ) : null}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-2xl border border-[var(--color-text-border)]/70 bg-white p-4 sm:p-5">
        <div>
          <p className={ui.sectionLabel}>{t("conveyanceClaim.claimTotal")}</p>
          <p className="text-xl font-extrabold text-foreground">
            {formatAmount(claimTotal, abbreviation)}
          </p>
          {overCap ? (
            <p className="mt-1 text-[11px] font-semibold text-rose-500">
              {t("conveyanceClaim.errorTotalOverCap", { max: MAX_TOTAL_AMOUNT })}
            </p>
          ) : null}
        </div>

        {linesError ? (
          <p className="text-[11px] font-semibold text-rose-600 max-w-[420px]">
            {t("conveyanceClaim.linesUnavailableNotice")}
          </p>
        ) : readOnly ? (
          <p className="text-[11px] text-[var(--color-text-secondary)] max-w-[360px]">
            {t("conveyanceClaim.readOnlyNotice")}
          </p>
        ) : (
          <div className="flex items-center gap-3 shrink-0">
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={saving}
              className="h-11 px-5 rounded-xl border border-[var(--color-text-border)] text-sm font-semibold text-foreground hover:bg-gray-50 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("conveyanceClaim.saveDraft")}
            </button>
            <button type="button" onClick={handleSubmit} disabled={saving} className={ui.primaryBtn}>
              {saving ? t("localConveyance.saving") : t("conveyanceClaim.submitClaim")}
            </button>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

export default RatePerKmClaimPage;
